// src/editor/specOps.ts
// Immutable edits on a make_video spec, for the visual editor. Every edit
// returns a new spec and leaves the old one alone (React state, undo).
// Fields the editor clears are deleted rather than set to "", so
// make_video's own defaults apply, except file fields, where "" means none.
// ─────────────────────────────────────────────────────────────────

import type { Snippet } from '@ring/shared/snippets';
import { DEFAULTS, deepMerge, type Json } from '../preview/spec';
import { END_FADE } from '../preview/timeline';

export type Path = (string | number)[];

export function getIn(obj: unknown, path: Path): unknown {
  let cur = obj;
  for (const key of path) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Json)[key as string];
  }
  return cur;
}

/**
 * A copy of `obj` with the value at `path` replaced; `undefined` deletes
 * the key (or removes the array item). Missing objects along the way are
 * created.
 */
export function setIn(obj: Json, path: Path, value: unknown): Json {
  const [key, ...rest] = path;
  const copy: Json = Array.isArray(obj) ? [...obj] as unknown as Json : { ...obj };
  if (rest.length === 0) {
    if (value !== undefined) copy[key as string] = value;
    else if (Array.isArray(copy)) (copy as unknown as unknown[]).splice(key as number, 1);
    else delete copy[key as string];
    return copy;
  }
  const child = copy[key as string];
  const base = child !== null && typeof child === 'object' ? child : (typeof rest[0] === 'number' ? [] : {});
  copy[key as string] = setIn(base, rest, value);
  return copy;
}

function beatsOf(spec: Json): Json[] {
  return Array.isArray(spec.beats) ? spec.beats : [];
}

export function moveBeat(spec: Json, from: number, to: number): Json {
  const beats = [...beatsOf(spec)];
  if (to < 0 || to >= beats.length) return spec;
  const [beat] = beats.splice(from, 1);
  beats.splice(to, 0, beat);
  return { ...spec, beats };
}

export function insertBeats(spec: Json, at: number, beats: Json[]): Json {
  const all = [...beatsOf(spec)];
  all.splice(at, 0, ...structuredClone(beats));
  return { ...spec, beats: all };
}

/** The fields of a beat that describe its (single) text box; they move into texts[0] when a second box is added. */
export const TEXT_BOX_FIELDS = ['text', 'y', 'anchor', 'fontSize', 'width', 'textStyle', 'pillColor', 'emphasis', 'css', 'start'] as const;

/** Turns a one-text beat into a `texts` beat (its text becomes the first box) and adds an empty box. */
export function addTextBox(spec: Json, beatIndex: number): Json {
  const beat: Json = { ...beatsOf(spec)[beatIndex] };
  let texts: Json[];
  if (Array.isArray(beat.texts)) {
    texts = [...beat.texts];
  } else {
    const first: Json = {};
    for (const k of TEXT_BOX_FIELDS) {
      if (k in beat) { first[k] = beat[k]; delete beat[k]; }
    }
    if (!('text' in first)) first.text = '';
    texts = [first];
  }
  texts.push({ text: '', start: 0 });
  beat.texts = texts;
  return setIn(spec, ['beats', beatIndex], beat);
}

/** Removes box `boxIndex`; a beat left with one box goes back to a plain text beat. */
export function removeTextBox(spec: Json, beatIndex: number, boxIndex: number): Json {
  const beat: Json = { ...beatsOf(spec)[beatIndex] };
  const texts = [...(beat.texts as Json[])];
  texts.splice(boxIndex, 1);
  if (texts.length > 1) {
    beat.texts = texts;
  } else {
    delete beat.texts;
    Object.assign(beat, texts[0] ?? { text: '' });
  }
  return setIn(spec, ['beats', beatIndex], beat);
}

/**
 * Inserts a snippet: a beats snippet adds its beats at `at` (default: the
 * end); any other kind is deep-merged over the spec, so a style snippet
 * changes only the fields it sets.
 */
export function applySnippet(spec: Json, snippet: Pick<Snippet, 'kind' | 'fragment'>, at?: number): Json {
  if (snippet.kind === 'beats') {
    const beats = Array.isArray(snippet.fragment.beats) ? snippet.fragment.beats as Json[] : [];
    return insertBeats(spec, at ?? beatsOf(spec).length, beats);
  }
  return deepMerge(spec, snippet.fragment);
}

/**
 * A good moment to show beat `i` in the preview: once everything in it has
 * appeared and faded in (its last text box or image to start, plus the
 * fade), but before it starts fading out. Its very first frame would show
 * the text only just starting to fade in. `i = -1` is the end card, shown
 * once its 0.4 s fade-in is done.
 */
export function showcaseTime(spec: Json, i: number): number {
  const { starts, endStart } = beatStartTimes(spec);
  if (i < 0) {
    const endDuration = typeof spec.endCard?.duration === 'number' ? spec.endCard.duration : DEFAULTS.endCard.duration;
    return endStart + Math.min(END_FADE, endDuration / 2);
  }
  const beat: Json | undefined = beatsOf(spec)[i];
  if (!beat) return 0;
  const duration = typeof beat.duration === 'number' && beat.duration > 0 ? beat.duration : 0;
  const fade: number = typeof spec.style?.fade === 'number' ? spec.style.fade : DEFAULTS.style.fade;
  const items: Json[] = [...(Array.isArray(beat.texts) ? beat.texts : [beat]), ...(Array.isArray(beat.images) ? beat.images : [])];
  const lastStart = Math.max(0, ...items.map(it => (typeof it.start === 'number' ? it.start : 0)));
  const visible = lastStart + Math.min(fade, (duration - lastStart) / 2);
  // fade-ins are capped at half the time shown, so `visible` is never past the start of the fade-out
  return starts[i] + Math.min(visible, duration);
}

/** Start time of each beat, and the time the end card starts. */
export function beatStartTimes(spec: Json): { starts: number[]; endStart: number } {
  const starts: number[] = [];
  let t = 0;
  for (const b of beatsOf(spec)) {
    starts.push(t);
    t += typeof b.duration === 'number' && b.duration > 0 ? b.duration : 0;
  }
  return { starts, endStart: t };
}
